import DateFnsUtils from "@date-io/date-fns";
import { DotLegalBreadCrumbProvider } from "@dotlegal/dotlegal-ui-components";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AuthProvider } from "oidc-react";
import { useEffect, useState } from "react";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import AnonymousRouterSwitch from "./AnonymousRouterSwitch";
import useAppStyles from "./App.styles";
import { getSelectedOrganizationFromLocalStorage, UserSessionInfo } from "./auth/AuthService";
import ChangeOrganisation from "./auth/ChangeOrganisation";
import { oidcConfig } from "./auth/OidcConfig";
import SignInCallback from "./auth/SignInCallback";
import SignOut from "./auth/SignOut";
import UserLoader from "./auth/UserLoader";
import { useStateLocalStorage } from "./common/hooks/useStateLocalStorage";
import DrawerMenu from "./core/drawer/DrawerMenu";
import { SelectedOrganization } from "./core/organizations/Organization.types";
import { ApplicationUser } from "./core/users/User.types";
import { setupErrorHandling } from "./errorHandling";
import ErrorRouterSwitch from "./ErrorRouterSwitch";
import "./extensions";
import Main from "./Main";
import SelectOrganization from "./pages/selectOrganization/SelectOrganization";
import "./pdf.css";
import QueryClientProviderIpPortal from "./QueryClientProviderIpPortal";

setupErrorHandling();
function App() {
    const classes = useAppStyles();
    const [mobileDrawerIsOpen, setMobileDrawerIsOpen] = useState(false);
    const [selectedOrg, setSelectedOrg] = useStateLocalStorage<SelectedOrganization | null>(process.env.REACT_APP_IPPORTAL_SELECTED_ORG!, null);
    const [verifiedUser, setVerifiedUser] = useState<ApplicationUser | null>(null);
    const userSession: UserSessionInfo = { selectedOrg, verifiedUser };

    const onSelectedOrganizationChanged = (org: SelectedOrganization | null) => {
        setSelectedOrg(org);
    };

    const onSelectedOrgChanged = () => {
        const fromStorage = getSelectedOrganizationFromLocalStorage();
        if (fromStorage) setSelectedOrg(fromStorage);
    };

    useEffect(() => {
        onSelectedOrgChanged();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <BrowserRouter>
                <QueryClientProviderIpPortal>
                    <LocalizationProvider dateAdapter={AdapterDateFns} utils={DateFnsUtils}>
                        <ErrorRouterSwitch>
                            <AnonymousRouterSwitch>
                                <DotLegalBreadCrumbProvider>
                                    <AuthProvider {...oidcConfig}>
                                        <UserLoader
                                            onUserSessionLoaded={(user) => {
                                                setVerifiedUser(user);
                                                const fromStorage = getSelectedOrganizationFromLocalStorage();
                                                if (fromStorage) setSelectedOrg(fromStorage);
                                            }}
                                            applicationUser={userSession.verifiedUser}
                                        >
                                            <div className={classes.root}>
                                                <DrawerMenu
                                                    mobileDrawerIsOpen={mobileDrawerIsOpen}
                                                    onMobileDrawerClose={() => setMobileDrawerIsOpen(false)}
                                                    userSession={userSession}
                                                />
                                                <Switch>
                                                    <Route path="/select-organisation">
                                                        <SelectOrganization
                                                            userSession={{
                                                                ...userSession,
                                                                onSelectedOrganizationChanged,
                                                            }}
                                                        />
                                                    </Route>
                                                    <Route path="/change-organisation/:id">
                                                        <ChangeOrganisation onOrgChanged={(org) => setSelectedOrg(org)} />
                                                    </Route>
                                                    <Route path="/sign-in-callback">
                                                        <SignInCallback
                                                            userSession={{
                                                                ...userSession,
                                                                onSelectedOrganizationChanged,
                                                            }}
                                                        />
                                                    </Route>
                                                    <Route path="/sign-out">
                                                        <SignOut />
                                                    </Route>
                                                    <Route path="*">
                                                        <Main
                                                            onDrawerExpandedMobile={() => setMobileDrawerIsOpen(true)}
                                                            onSelectedOrgChanged={onSelectedOrgChanged}
                                                            userSession={{
                                                                ...userSession,
                                                                onSelectedOrganizationChanged,
                                                            }}
                                                        />
                                                    </Route>
                                                </Switch>
                                            </div>
                                        </UserLoader>
                                    </AuthProvider>
                                </DotLegalBreadCrumbProvider>
                            </AnonymousRouterSwitch>
                        </ErrorRouterSwitch>
                    </LocalizationProvider>
                </QueryClientProviderIpPortal>
            </BrowserRouter>
        </>
    );
}

export default App;
